import { Fragment, VFC, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../config/api';

import {
  H2,
  StyledPageContainer,
  StyledPageContent,
  StyledPageWrapper,
} from '../../components/styles';
import { Politician, PoliticianEntry } from './PoliticianEntry/PoliticianEntry';
import { Hero } from '../../components/Hero';
import { CustomCircular } from '../../components/Progress/CustomCircular';
import { correctName } from 'pages/Detail/Visualization/Visualization';

export const Absence: VFC = () => {
  const [periods, setPeriods] = useState<Array<any>>([]);
  const [selectedPeriod, selectPeriod] = useState(0);

  useEffect(() => {
    const loadData = async () => {
      const [absenceResponse, partiesResponse] = await Promise.all([
        api.get(
          `absences?populate[0]=representatives&populate[1]=representatives.representative`
        ),
        api.get(`party-details`),
      ]);
      const absences = absenceResponse.data.data;
      const parties = partiesResponse.data.data.map(
        (party: any) => party.attributes
      );

      const allPeriods = [];
      for (const period of absences) {
        const { dateFrom, dateTo } = period.attributes;
        const voters = period.attributes.representatives;
        voters.sort((a: any, b: any) => b.percentage - a.percentage);

        for (const voter of voters) {
          const party = parties.find(
            (party: any) =>
              party.name === voter?.representative?.data?.attributes?.party
          );

          voter.partyColor = party ? party.color : 'white';
        }

        allPeriods.push({
          voters,
          from: new Date(dateFrom),
          to: new Date(dateTo),
        });
      }

      setPeriods(allPeriods);
      selectPeriod(allPeriods.length - 1);
    };

    loadData();
  }, []);

  const politicians = processAndCleanPoliticians(
    periods[selectedPeriod]?.voters ?? []
  );

  return (
    <Fragment>
      <StyledPageWrapper>
        <Hero>
          <StyledPageContainer>
            <H2>Docházka zastupitelů</H2>
            <p>
              Na základě dat z hlasování brněnského zastupitelstva jsme
              spočítali docházku jednotlivých zastupitelů. U každého zastupitele
              je uveden celkový počet hlasování, na kterých byl přítomen, a
              celkový počet hlasování, u kterých je uveden jako nepřítomen.
              Výsledné procento pak znázorňuje celkovou procentuální účast.
            </p>
            <p>
              Zdrojová data nejsou dostatečně kvalitní, aby bylo možné k sobě
              přiřadit např. záznamy zastupitelů, kteří během volebního období
              změnili stranu. Některá jména se tak mohou v seznamu objevit
              vícekrát. Není také možné určit absenci zastupitele u hlasování,
              pokud není ve zdrojových datech vůbec uveden.
            </p>
            <p>
              Více informací o zdrojových datech najdete na stránce{' '}
              <Link style={{ color: 'black' }} to="/about">
                O PROJEKTU
              </Link>
              .
            </p>
          </StyledPageContainer>
        </Hero>
        <StyledPageContainer>
          <StyledPageContent>
            {periods.length === 0 && <CustomCircular />}
            {politicians.length > 0 &&
              politicians.map((politician: any) => (
                <PoliticianEntry key={politician.id} politician={politician} />
              ))}
          </StyledPageContent>
        </StyledPageContainer>
      </StyledPageWrapper>
    </Fragment>
  );
};

const processAndCleanPoliticians = (politicians: any[]): any[] => {
  const pMap = new Map<string, Politician>();
  for (const politician of politicians) {
    const info = getPoliticianInfo(politician);
    if (pMap.has(info.id)) {
      const prevInfo = pMap.get(info.id)!;
      prevInfo.attendance.present += info.attendance.present;
      prevInfo.attendance.missing += info.attendance.missing;
      prevInfo.attendance.percentage = countPercentage(
        prevInfo.attendance.present,
        prevInfo.attendance.missing
      );
    } else {
      pMap.set(info.id, info);
    }
  }

  return Array.from(pMap.values()).sort(politicianComparator);
};

const getPoliticianInfo = (politician: any): Politician => {
  const originalName: string =
    politician?.representative?.data?.attributes?.name ?? '';
  const correctedName = correctName(originalName);
  const partyName: string =
    politician?.representative?.data?.attributes?.party ?? '';

  return {
    id: `${correctedName}:${partyName}`,
    originalName,
    correctedName,
    isWoman: originalName.trim().endsWith('á'),
    party: {
      name: partyName,
      color: politician.partyColor as string,
    },
    attendance: {
      present: (politician.present as number) ?? 0,
      missing: (politician.missing as number) ?? 0,
      percentage: (politician.percentage as number) ?? 0,
    },
  };
};

const politicianComparator = (a: Politician, b: Politician) => {
  const pDiff = b.attendance.percentage - a.attendance.percentage;
  if (pDiff) {
    return pDiff;
  }
  return b.attendance.present - a.attendance.present;
};

// stejna implementace jako na BE
const countPercentage = (present: number, missing: number): number => {
  return parseInt(((present / (present + missing)) * 100).toFixed(2));
};
